// Shared GS delay bus, from native Web Audio nodes — no worklet needed.
//
// The Sound Canvas delay block (SysEx 40 01 50-5A): a 3-tap delay — center tap with a
// feedback loop, left/right taps at ratios of the center time — fed per voice by CC 94
// and the per-note drum NRPN 0x1F, with its output also sent on to the reverb
// (40 01 5A). SF2 has no delay-send generator, so unlike reverb/chorus the sends here
// come only from the MIDI stream.
//
// Tables and scaling are ADOPTED from TiMidity++ (tables.c `delay_time_center_table`,
// `delay_macro_presets`; readmidi.c `recompute_delay_status_gs`), which Roland never
// published: time center 0x00-0x73 → 0.1-1000 ms, time ratio v/24, tap level
// level*levelN/127², feedback (v-64)·0.01526, send-to-reverb v·0.00787. TiMidity's
// ×3.25 MASTER_DELAY_LEVEL is its own mixer headroom compensation and is NOT adopted —
// our sends feed the bus at voice level like the reverb/chorus sends do.
// The table's 0x61 entry is 340 ms, matching the SC-88 documented power-on default.

clamp := (v: number, lo: number, hi: number) -> Math.min Math.max(v, lo), hi

// 0x00-0x73 → ms (TiMidity++ tables.c, verbatim)
DELAY_TIME_CENTER_MS := [
  0.1, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9,
  2.0, 2.2, 2.4, 2.6, 2.8, 3.0, 3.2, 3.4, 3.6, 3.8, 4.0, 4.2, 4.4, 4.6, 4.8,
  5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0, 9.5,
  10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
  20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46, 48,
  50, 55, 60, 65, 70, 75, 80, 85, 90, 95,
  100, 110, 120, 130, 140, 150, 160, 170, 180, 190,
  200, 220, 240, 260, 280, 300, 320, 340, 360, 380, 400, 420, 440, 460, 480,
  500, 550, 600, 650, 700, 750, 800, 850, 900, 950, 1000,
]

// GS delay macros 0-9 (40 01 50): raw wire values in SysEx-address order
// [preLpf, timeC, ratioL, ratioR, levelC, levelL, levelR, level, feedback, reverbSend]
// (TiMidity++ tables.c `delay_macro_presets`).
export DELAY_MACROS: Record<string, number>[] := [
  { preLpf: 0, timeC:  97, ratioL:  1, ratioR:  1, levelC: 127, levelL:   0, levelR:  0, level: 64, feedback: 79, reverbSend:  0 } // Delay1
  { preLpf: 0, timeC: 106, ratioL:  1, ratioR:  1, levelC: 127, levelL:   0, levelR:  0, level: 64, feedback: 79, reverbSend:  0 } // Delay2
  { preLpf: 0, timeC: 115, ratioL:  1, ratioR:  1, levelC: 127, levelL:   0, levelR:  0, level: 64, feedback: 63, reverbSend:  0 } // Delay3
  { preLpf: 0, timeC:  83, ratioL:  1, ratioR:  1, levelC: 127, levelL:   0, levelR:  0, level: 64, feedback: 71, reverbSend:  0 } // Delay4
  { preLpf: 0, timeC:  90, ratioL: 12, ratioR: 24, levelC:   0, levelL: 125, levelR: 60, level: 64, feedback: 73, reverbSend:  0 } // Pan Delay1
  { preLpf: 0, timeC: 109, ratioL: 12, ratioR: 24, levelC:   0, levelL: 125, levelR: 60, level: 64, feedback: 70, reverbSend:  0 } // Pan Delay2
  { preLpf: 0, timeC: 115, ratioL: 12, ratioR: 24, levelC:   0, levelL: 120, levelR: 64, level: 64, feedback: 72, reverbSend:  0 } // Pan Delay3
  { preLpf: 0, timeC:  93, ratioL: 12, ratioR: 24, levelC:   0, levelL: 120, levelR: 64, level: 64, feedback: 63, reverbSend:  0 } // Pan Delay4
  { preLpf: 0, timeC: 109, ratioL: 12, ratioR: 24, levelC:   0, levelL: 114, levelR: 60, level: 64, feedback: 60, reverbSend: 36 } // Delay to Reverb
  { preLpf: 0, timeC: 110, ratioL: 21, ratioR: 31, levelC:  97, levelL: 127, levelR: 67, level: 64, feedback: 39, reverbSend:  0 } // Pan Repeat
]

// GS power-on state (SC-88 owner's manual; TiMidity `init_delay_status_gs`). The bus is
// silent regardless until a send asks for it — CC 94 and drum NRPN 0x1F default to 0.
export DELAY_DEFAULTS: Record<string, number> :=
  { preLpf: 0, timeC: 0x61, ratioL: 1, ratioR: 1, levelC: 0x7F, levelL: 0, levelR: 0, level: 0x40, feedback: 0x50, reverbSend: 0 }

export delayTimeCenterSeconds := (v: number) ->
  DELAY_TIME_CENTER_MS[clamp Math.round(v), 0, 0x73] / 1000

// Pure GS→engine scalings, exported so the calibration tests can pin them.
export delayFeedbackRatio := (v: number) ->
  clamp (v - 64) * 0.01526, -0.9, 0.9

export delayTapLevel := (level: number, tap: number) ->
  level * tap / (127 * 127)

export delayReverbSendRatio := (v: number) ->
  v * 0.00787

export type GsDelayNode = {
  input: GainNode
  output: GainNode
  /** Post-tap send toward the reverb bus (40 01 5A); the channel graph connects it. */
  reverbSend: GainNode
  setParams: (p: Record<string, number>) => void
  dispose: () => void
}

// Ratio taps can reach 5× the 1s table max; size the lines once for the worst case.
MAX_DELAY_S := 5.5

export createDelayNode := (context: BaseAudioContext): GsDelayNode ->
  input := context.createGain()
  output := context.createGain()

  // Pre-LPF (40 01 51): one-pole on hardware; a first-order-ish biquad parked at 20kHz
  // when off. Always in-line so toggling it never rewires the graph.
  lpf := context.createBiquadFilter()
  lpf.type = "lowpass"
  lpf.frequency.value = 20000
  lpf.Q.value = -3.01 // ~0.707 in dB terms: no resonant bump, closest to one-pole
  input.connect lpf

  taps := ["C", "L", "R"].map ->
    delay := context.createDelay MAX_DELAY_S
    level := context.createGain()
    level.gain.value = 0
    lpf.connect delay
    delay.connect level
    level.connect output
    { delay, level }

  // Feedback regenerates the center tap only, matching the hardware/TiMidity topology.
  feedback := context.createGain()
  feedback.gain.value = 0
  taps[0].delay.connect feedback
  feedback.connect taps[0].delay

  reverbSend := context.createGain()
  reverbSend.gain.value = 0
  output.connect reverbSend

  // Raw GS wire values; recomputed as a whole because tap times and levels are products
  // of more than one parameter (timeC×ratio, level×levelN).
  state := {...DELAY_DEFAULTS}

  apply := ->
    t := context.currentTime
    center := delayTimeCenterSeconds state.timeC
    times := [center, center * state.ratioL / 24, center * state.ratioR / 24]
    levels := [state.levelC, state.levelL, state.levelR].map (l) ->
      delayTapLevel state.level, l
    taps.forEach ({delay, level}, i) ->
      delay.delayTime.setValueAtTime clamp(times[i], 0.0001, MAX_DELAY_S), t
      level.gain.setValueAtTime levels[i], t
    feedback.gain.setValueAtTime delayFeedbackRatio(state.feedback), t
    reverbSend.gain.setValueAtTime delayReverbSendRatio(state.reverbSend), t
    lpf.frequency.setValueAtTime (state.preLpf > 0 ? (7 - state.preLpf) / 7 * 16000 + 200 : 20000), t

  setParams := (p: Record<string, number>) ->
    Object.assign state, p
    apply()

  apply()

  input: input
  output: output
  reverbSend: reverbSend
  setParams: setParams
  dispose: ->
    input.disconnect()
    output.disconnect()
    reverbSend.disconnect()
